import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import CreateAccount from "../components/CreateAccount";
import EditAccount from "../components/EditAccount";
import DeleteAccountModal from "../components/DeleteAccountModal";
import "../Console.css";

/* ------------------ Inline styles for grouped card UI ------------------ */
const styles = (
  <style>{`
    .sections { display:flex; flex-direction:column; gap:18px; }

    .section {
      background:#ffffff;
      border:1px solid #e5e7eb;
      border-radius:12px;
      padding:12px 14px;
      box-shadow:0 1px 2px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04);
    }

    .section-title {
      display:flex; align-items:center; gap:10px;
      font-size:14px; font-weight:800; color:#111827; margin:2px 0 10px 2px;
      letter-spacing:.2px;
    }
    .section-title .badge {
      display:inline-flex; align-items:center; justify-content:center;
      width:24px; height:24px; border-radius:9999px;
      background:#f3f4f6; color:#374151; font-size:12px; font-weight:800;
    }
    .section-title small {
      color:#6b7280; font-weight:700; font-size:12px;
    }

    .accounts-grid { display:grid; gap:12px; grid-template-columns: repeat(3, minmax(0,1fr)); }
    @media (max-width: 1200px) { .accounts-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } }
    @media (max-width: 760px)  { .accounts-grid { grid-template-columns: 1fr; } }

    .account-card {
      background:#fff; border:1px solid #e5e7eb; border-radius:14px;
      padding:14px; box-shadow:0 1px 2px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04);
      color:#111827; font-size:13px; line-height:1.35;
      position: relative;
    }

    .card-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:8px; }
    .head-left { display:flex; align-items:center; gap:10px; min-width:0; }

    .avatar {
      width:40px; height:40px; border-radius:9999px; overflow:hidden;
      display:flex; align-items:center; justify-content:center;
      font-weight:800; font-size:13px; color:#fff; text-transform:uppercase;
      flex: 0 0 40px;
      border: 1px solid rgba(0,0,0,0.05);
    }
    .avatar img { width:100%; height:100%; object-fit:cover; display:block; }

    .name-email { min-width:0; }
    .display-name {
      font-weight:800; font-size:16px; line-height:1.15; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }
    .email {
      font-size:12px; color:#374151; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }
    .email a { color:#2563eb; text-decoration:none; }
    .email a:hover { text-decoration:underline; }

    .meta-row { display:flex; flex-wrap:wrap; gap:8px; margin:8px 0; }
    .tag {
      display:inline-flex; align-items:center; gap:6px; padding:3px 8px;
      border-radius:9999px; background:#eef2ff; color:#1f2937; font-size:11px; font-weight:700;
    }
    .chip {
      display:inline-flex; align-items:center; gap:6px; padding:2px 6px;
      border-radius:8px; background:#f9fafb; color:#6b7280; font-size:11px;
    }
    .perm-chip {
      display:inline-flex; align-items:center; gap:6px; padding:3px 8px;
      border-radius:9999px; background:#f3f4f6; color:#111827; font-size:11px;
    }

    .divider { height:1px; background:#e5e7eb; opacity:.85; margin:10px 0; }

    /* Actions menu */
    .card-actions { position: relative; }
    .icon-ellipsis {
      display:inline-flex; align-items:center; justify-content:center;
      width:36px; height:36px; border-radius:10px;
      background:#fff; color:#111827; border:1px solid #e5e7eb; font-weight:700; font-size:14px;
    }
    .icon-ellipsis:hover { background:#f9fafb; }
    .icon-ellipsis:focus { outline: 2px solid #d1d5db; }

    .menu-pop {
      position:absolute; top:38px; right:0;
      background:#fff; border:1px solid #e5e7eb; border-radius:12px;
      box-shadow:0 10px 30px rgba(0,0,0,0.08);
      min-width: 180px; padding:6px;
      z-index: 50;
    }
    .menu-item {
      width:100%; display:flex; align-items:center; gap:10px;
      border:none; background:transparent; text-align:left;
      padding:8px 10px; border-radius:8px; font-size:13px; color:#111827; font-weight:700;
    }
    .menu-item i { font-size:14px; }
    .menu-item:hover { background:#f3f4f6; }
    .menu-item.danger { color:#b91c1c; }
    .menu-item.danger:hover { background:#fff1f2; }
    .menu-item:disabled {
      opacity:.6; cursor:not-allowed;
    }

    .empty-state {
      background:#fff; border:1px dashed #e5e7eb; border-radius:12px; padding:24px;
      text-align:center; color:#6b7280; font-size:13px;
    }
  `}</style>
);

/* ------------------ Helpers ------------------ */
function formatPermissions(permsIn) {
  if (!permsIn) return [];

  let perms = permsIn;

  // Handle legacy shapes
  if (perms instanceof Map) {
    perms = Object.fromEntries(perms);
  } else if (typeof perms === "string") {
    try {
      perms = JSON.parse(perms);
    } catch {
      return [];
    }
  }

  if (typeof perms !== "object" || perms === null) return [];

  // Coerce truthy values robustly
  const isTrue = (v) => v === true || v === 1 || v === "1" || v === "true";

  return Object.keys(perms).filter((k) => isTrue(perms[k]));
}

const pretty = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : "Unknown");

function sectionTitle(key) {
  const map = {
    superadmin: "Supers",
    crmmanager: "CRM Admins",
    customadmin: "Custom Admins",
    backoffice: "Back-Office Admins",
    brandadmin: "Brand Admins",
  };
  const k = (key || "").toLowerCase();
  return map[k] || pretty(key);
}

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return "U";
  const base = nameOrEmail.split("@")[0];
  const parts = base.trim().split(/\s+|[_.-]+/);
  const letters = parts
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]);
  return letters.join("").toUpperCase() || "U";
}

function getColorForString(str) {
  const colors = [
    "#4f46e5",
    "#0ea5e9",
    "#22c55e",
    "#f59e0b",
    "#ef4444",
    "#a855f7",
    "#14b8a6",
    "#f97316",
  ];
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return colors[Math.abs(hash) % colors.length];
}

/* ------------------ Component ------------------ */
const Accounts = ({ domain, userData }) => {
  const [users, setUsers] = useState([]);
  // derive current user type and organization id from userData tuple
  const session = useMemo(() => {
    return {
      id: userData?.[0] || "",
      type: (userData?.[1] || "").toLowerCase(),
      orgId: userData?.[5] || "",
    };
  }, [userData]);

  // which card's menu is open
  const [openMenuId, setOpenMenuId] = useState(null);

  const [internalExpanded, setInternalExpanded] = useState(false);

  // edit modal state
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [editUserId, setEditUserId] = useState("");

  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const handleCloseCreateAccountModal = () => setShowCreateAccountModal(false);
  const handleShowCreateAccountModal = () => setShowCreateAccountModal(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null); // { _id, username }

  const fetchUsers = () => {
    const base = "/api/users";
    const isBrandAdmin = session.type === "brandadmin";
    const looksLikeObjectId =
      typeof session.orgId === "string" &&
      session.orgId.length === 24 &&
      /^[a-f0-9]+$/i.test(session.orgId);

    const url =
      isBrandAdmin && looksLikeObjectId
        ? `${base}/admins/by-client/${session.orgId}`
        : `${base}/admins`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => setUsers(data || []))
      .catch((err) => console.error("Error fetching admins:", err));
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.type, session.orgId]);

  const handleFormSubmit = () => {
    fetchUsers();
  };

  const handleDeleteUser = (user) => {
    setDeleteUser({ _id: user._id, username: user.username });
    setShowDeleteModal(true);
  };

  const handleEditUser = (user) => {
    setOpenMenuId(null);
    setEditUserId(user._id); // pass row id
    setShowEditAccountModal(true); // open modal
  };

  const handleCloseEditAccountModal = () => {
    setShowEditAccountModal(false);
    setEditUserId("");
  };

  // close menus on any outer click
  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  /* ----- Groups & order ----- */
  const groups = useMemo(() => {
    const g = new Map();
    users.forEach((u) => {
      const t = (u.type || "other").toLowerCase();
      if (!g.has(t)) g.set(t, []);
      g.get(t).push(u);
    });
    return g;
  }, [users]);

  const brandAdminList = useMemo(() => groups.get("brandadmin") || [], [groups]);

  const internalTypes = useMemo(() => {
    const preferred = ["superadmin", "crmmanager", "customadmin", "backoffice"];
    const existing = Array.from(groups.keys());
    const others = existing
      .filter((t) => !preferred.includes(t) && t !== "brandadmin")
      .sort((a, b) => a.localeCompare(b));
    return preferred.concat(others).filter((t) => groups.has(t));
  }, [groups]);

  const internalCount = useMemo(
    () => internalTypes.reduce((sum, t) => sum + (groups.get(t) || []).length, 0),
    [internalTypes, groups]
  );

  const renderCard = (user) => {
    const orgTypePretty = user.organizationType ? pretty(user.organizationType) : "N/A";
    const perms = formatPermissions(user.permissions);
    const isSuperadmin = (user.type || "").toLowerCase() === "superadmin";
    const displaySeed = user.username || user.email || user._id || "User";
    const initials = getInitials(displaySeed);
    const bg = getColorForString(displaySeed);
    const photo = user.organizationLogo || user.avatarUrl || user.photoUrl || user.imageUrl;
    const menuOpen = openMenuId === user._id;

    return (
      <article
        key={user._id}
        className="account-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-head">
          <div className="head-left">
            <div
              className="avatar"
              style={{ background: photo ? "transparent" : bg }}
              aria-label={`Avatar of ${user.username || user.email || "user"}`}
            >
              {photo ? <img src={photo} alt="" /> : <span>{initials}</span>}
            </div>
            <div className="name-email" title={user.username || ""}>
              <div className="display-name">{user.username || "—"}</div>
              <div className="email" title={user.email || ""}>
                {user.email ? (
                  <a href={`mailto:${user.email}`}>{user.email}</a>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </div>

          <div className="card-actions">
            {user._id !== session.id && (
              <button
                className="icon-ellipsis"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                title="Actions"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(menuOpen ? null : user._id);
                }}
              >
                <i className="fas fa-ellipsis-h" />
              </button>
            )}
            {menuOpen && (
              <div
                className="menu-pop"
                role="menu"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="menu-item"
                  role="menuitem"
                  onClick={() => handleEditUser(user)}
                  disabled={isSuperadmin}
                  title={isSuperadmin ? "Cannot edit superadmin" : "Edit account"}
                >
                  <i className="fas fa-pen-to-square" />
                  Edit
                </button>
                <button
                  className="menu-item danger"
                  role="menuitem"
                  onClick={() => {
                    if (isSuperadmin) return;
                    setOpenMenuId(null);
                    handleDeleteUser(user);
                  }}
                  disabled={isSuperadmin}
                  title={isSuperadmin ? "Cannot delete superadmin" : "Delete account"}
                >
                  <i className="fas fa-trash-alt" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="divider" />

        <div className="meta-row">
          <span className="tag" title={`Type: ${user.type || "—"}`}>
            <i className="fas fa-user-shield" />
            {user.type || "—"}
          </span>
          <span className="chip" title={`Client: ${user.organization || "—"}`}>
            <i className="fas fa-building" />
            {user.organization || "—"}
          </span>
          <span className="chip" title={`Org. Type: ${orgTypePretty}`}>
            <i className="fas fa-sitemap" />
            {orgTypePretty}
          </span>
          {user._id && (
            <span className="chip" title={user._id}>
              ID: {String(user._id).slice(-6)}
            </span>
          )}
        </div>

        <div className="divider" />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {perms.length ? (
            perms.map((p) => (
              <span className="perm-chip" key={p}>
                <i className="fas fa-key" />
                {p}
              </span>
            ))
          ) : (
            <span className="chip">No explicit permissions</span>
          )}
        </div>
      </article>
    );
  };

  return (
    <>
      <Helmet>
        <title>Users | BLEEP</title>
        <meta
          name="description"
          content="Introducing BLEEP, the revolutionary crowdsourcing app designed to combat counterfeit products and services."
        />
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div
          style={{
            flex: 1,
            height: "calc(100vh - 94px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,0,0,1) 100%)",
              overflow: "hidden",
            }}
          >
            {/* Decorative overlay */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            {/* Content */}
            <div
              className="row d-flex align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div
                className="col"
                style={{ padding: 10 }}
              >
                <div className="col d-flex align-items-center">
                  <b
                    style={{
                      fontSize: "28px",
                      textShadow: "#1c1c1c 1px 2px 0px",
                    }}
                  >
                    <i className="fas fa-key" /> Accounts
                  </b>
                </div>
                <p style={{ paddingTop: 8, textShadow: "#1c1c1c 1px 1px 0px" }}>
                  Manage Administrator & Client accounts
                </p>
                <button
                  className="btn btn-light btn-lg"
                  style={{
                    borderRadius: "30px",
                    boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px",
                    paddingBlock: "10px",
                    paddingInline: "30px",
                  }}
                  onClick={handleShowCreateAccountModal}
                >
                  <span style={{ marginRight: 10 }}>
                    <i className="fa-solid fa-plus-circle" />
                  </span>
                  <b>Create account</b>
                </button>
              </div>
            </div>
          </header>

          {/* Grouped Sections */}
          <div
            className="p-4"
            style={{ backgroundColor: "#ebebeb" }}
          >
            {users.length === 0 ? (
              <div className="empty-state">No users yet.</div>
            ) : (
              <div className="sections">

                {/* Brand Admins — always visible */}
                {brandAdminList.length > 0 && (
                  <section className="section">
                    <h3 className="section-title">
                      <span className="badge">
                        <i className="fas fa-th-large" />
                      </span>
                      Brand Admins
                      <small>&nbsp;•&nbsp;{brandAdminList.length}</small>
                    </h3>
                    <div className="accounts-grid">
                      {brandAdminList.map(renderCard)}
                    </div>
                  </section>
                )}

                {/* Internal Users — collapsible, collapsed by default */}
                {internalCount > 0 && (
                  <section className="section">
                    <button
                      type="button"
                      onClick={() => setInternalExpanded((prev) => !prev)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        width: "100%", border: "none", background: "transparent",
                        cursor: "pointer", padding: 0, textAlign: "left",
                        marginBottom: internalExpanded ? 14 : 6,
                      }}
                    >
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 24, height: 24, borderRadius: "9999px",
                        background: "#f3f4f6", color: "#374151", fontSize: 12, flexShrink: 0,
                      }}>
                        <i className="fas fa-users" />
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: "#111827", letterSpacing: ".2px" }}>
                        Internal Users
                      </span>
                      <small style={{ color: "#6b7280", fontWeight: 700, fontSize: 12 }}>
                        &nbsp;•&nbsp;{internalCount}
                      </small>
                      <i
                        className={`fas fa-chevron-${internalExpanded ? "up" : "down"}`}
                        style={{ color: "#9ca3af", fontSize: 11, marginLeft: "auto" }}
                      />
                    </button>

                    {!internalExpanded && (
                      <div style={{ fontSize: 12, color: "#9ca3af", paddingLeft: 34, marginBottom: 2 }}>
                        Click to reveal superadmins, CRM managers, and other internal accounts
                      </div>
                    )}

                    {internalExpanded && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                        {internalTypes.map((typeKey) => {
                          const list = groups.get(typeKey) || [];
                          if (!list.length) return null;
                          return (
                            <div key={typeKey}>
                              <div style={{
                                fontSize: 11, fontWeight: 700, color: "#9ca3af",
                                textTransform: "uppercase", letterSpacing: ".6px",
                                marginBottom: 8,
                              }}>
                                {sectionTitle(typeKey)}&nbsp;·&nbsp;{list.length}
                              </div>
                              <div className="accounts-grid">
                                {list.map(renderCard)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                )}

              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateAccount
        domain={domain}
        show={showCreateAccountModal}
        handleClose={handleCloseCreateAccountModal}
        handleFormSubmit={handleFormSubmit}
        clientId={session.type === "brandadmin" ? session.orgId : null}
      />

      <EditAccount
        domain={domain}
        show={showEditAccountModal}
        userId={editUserId} // pass the row id
        handleClose={handleCloseEditAccountModal}
        handleFormSubmit={() => {
          fetchUsers(); // refresh after save
          handleCloseEditAccountModal();
        }}
        clientId={session.type === "brandadmin" ? session.orgId : null}
      />

      <DeleteAccountModal
        show={showDeleteModal}
        handleClose={() => { setShowDeleteModal(false); setDeleteUser(null); }}
        userId={deleteUser?._id}
        username={deleteUser?.username}
        onDeleted={fetchUsers}
      />
    </>
  );
};

export default Accounts;
